
import { captureException, captureMessage } from '@sentry/react';

/**
 * Environment Variables for Logging
 *
 * This logging utility uses the following environment variables to control logging behavior:
 *
 * SENTRY_ENABLED (VITE_SENTRY_ENABLED):
 *   - Set to 'true' to enable external logging to Sentry
 *   - Set to 'false' or leave unset to disable Sentry logging
 *   - Example: VITE_SENTRY_ENABLED=true
 *
 * ENVIRONMENT (VITE_ENVIRONMENT):
 *   - Controls whether console logs are displayed in the browser
 *   - Set to 'production' to suppress all console logging
 *   - Set to 'development', 'staging', or any other value to show console logs
 *   - Example: VITE_ENVIRONMENT=production
 *
 * LOGGING_LEVEL (VITE_LOGGING_LEVEL):
 *   - Controls which log levels are sent to Sentry (when enabled)
 *   - Valid levels: 'debug', 'info', 'warning', 'error'
 *   - Only logs at or above the specified level will be sent to Sentry
 *   - Example: VITE_LOGGING_LEVEL=warning (sends warning and error logs only)
 *
 * Usage Examples:
 *   - Development with full logging: VITE_SENTRY_ENABLED=false, VITE_ENVIRONMENT=development, VITE_LOGGING_LEVEL=debug
 *   - Production with error-only Sentry: VITE_SENTRY_ENABLED=true, VITE_ENVIRONMENT=production, VITE_LOGGING_LEVEL=error
 *   - Staging with warning+ logs: VITE_SENTRY_ENABLED=true, VITE_ENVIRONMENT=staging, VITE_LOGGING_LEVEL=warning
 */

const SENTRY_ENABLED = import.meta.env.VITE_SENTRY_ENABLED === 'true';
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'development';
const LOGGING_LEVEL = import.meta.env.VITE_LOGGING_LEVEL || 'debug';

const loggingLevels = ['debug', 'info', 'warning', 'error'];
const currentLoggingLevel = loggingLevels.indexOf(LOGGING_LEVEL);

/**
 * Logs an error with optional Sentry reporting and error throwing.
 * @param {Object} options - The logging options.
 * @param {string} options.label - A label to identify the error source.
 * @param {Error} options.error - The error object to log.
 * @param {string} options.message - Additional error message.
 * @param {Object} [options.extraData] - Extra data to include with the log.
 * @param {boolean} [options.throwError=false] - Whether to re-throw the error after logging.
 */
export const logError = ({label, error, message, extraData, throwError = false}) => {
  if (ENVIRONMENT !== 'production') {
    console.warn(`${label} ${error}. Message: ${message}. Extra data: ${JSON.stringify(extraData)}`);
  }

  if (SENTRY_ENABLED) {
    captureException(error, {
      extra: extraData
    });
  }

  if (throwError) {
    throw error;
  }
};

/**
 * Logs a warning with optional Sentry reporting.
 * @param {Object} options - The logging options.
 * @param {string} options.label - A label to identify the warning source.
 * @param {string} options.warning - The warning message.
 * @param {string} options.message - Additional warning message.
 * @param {Object} [options.extraData] - Extra data to include with the log.
 */
export const logWarning = ({label, warning, message, extraData}) => {
  if (ENVIRONMENT !== 'production') {
    console.warn(label, warning, message, extraData);
  }

  if (SENTRY_ENABLED && currentLoggingLevel <= loggingLevels.indexOf('warning')) {
    captureMessage(warning, {
      extra: extraData
    });
  }
};

/**
 * Logs an info message with optional Sentry reporting.
 * @param {Object} options - The logging options.
 * @param {string} options.label - A label to identify the info source.
 * @param {string} options.message - Additional info message.
 * @param {Object} [options.extraData] - Extra data to include with the log.
 */
export const logInfo = ({label, message, extraData}) => {
  if (ENVIRONMENT !== 'production') {
    console.info(label, message, extraData);
  }

  if (SENTRY_ENABLED && currentLoggingLevel <= loggingLevels.indexOf('info')) {
    captureMessage(message, {
      extra: extraData
    });
  }
};